import { resolveGraphqlStatus, type GraphqlErrorItem } from "$lib/api/errors";

export class GraphqlError extends Error {
  readonly errors: readonly GraphqlErrorItem[];
  /** HTTP status from the transport response and/or GraphQL ``extensions.status``. */
  readonly status?: number;

  constructor(
    message: string,
    errors: readonly GraphqlErrorItem[],
    status?: number,
  ) {
    super(message);
    this.name = "GraphqlError";
    this.errors = errors;
    this.status = resolveGraphqlStatus(errors, status);
  }

  get code(): string | null {
    const code = this.errors[0]?.extensions?.code;
    return typeof code === "string" ? code : null;
  }

  get details(): Record<string, unknown> | null {
    const details = this.errors[0]?.extensions?.details;
    if (details && typeof details === "object" && !Array.isArray(details)) {
      return details as Record<string, unknown>;
    }
    return null;
  }

  get isUnauthorized(): boolean {
    if (this.status === 401) return true;
    return this.errors.some((e) => {
      const code = e.extensions?.code;
      return (
        code === "AuthenticationError" ||
        code === "UNAUTHENTICATED" ||
        /unauthori[sz]ed|not authenticated|invalid token|expired/i.test(e.message)
      );
    });
  }

  get isForbidden(): boolean {
    if (this.status === 403) return true;
    return this.code === "AuthorizationError";
  }

  get isNotFound(): boolean {
    if (this.status === 404) return true;
    return this.code === "NotFoundError";
  }

  get isConflict(): boolean {
    if (this.status === 409) return true;
    return (
      this.code === "ConflictError" ||
      this.code === "OPTIMISTIC_LOCK_ERROR" ||
      this.code === "WORKFLOW_LOCKED"
    );
  }

  get isInsufficientCredits(): boolean {
    if (this.status === 402) return true;
    return this.code === "InsufficientCredits";
  }
}
